import { ArrowLeft, Activity, MessageCircle } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { useNavigate } from "react-router-dom";

const LifestyleSynergyDetail = () => {
  const navigate = useNavigate();
  const score = 68;

  const getInsight = (score: number) => {
    if (score >= 80) {
      return {
        emoji: "🏆",
        title: "You're in peak lifestyle sync!",
        message:
          "Your activity, sleep, and recovery are all working together beautifully. Your body is well-rested, active, and showing excellent stress resilience. Maintain this rhythm.",
        color: "text-chart-2",
        bg: "bg-chart-2/10 border-chart-2/30",
      };
    }
    if (score >= 60) {
      return {
        emoji: "💪",
        title: "Your lifestyle is on track!",
        message:
          "You're maintaining a healthy balance of movement, rest, and recovery. Your body is coping well with daily demands — a bit more consistent sleep could push you into the excellent range.",
        color: "text-chart-2",
        bg: "bg-chart-2/10 border-chart-2/30",
      };
    }
    if (score >= 40) {
      return {
        emoji: "😴",
        title: "Lifestyle balance is moderate",
        message:
          "Your activity or recovery patterns may be inconsistent. Try prioritizing sleep quality and adding at least 20–30 minutes of moderate movement daily to improve your synergy score.",
        color: "text-yellow-600",
        bg: "bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-700/30",
      };
    }
    return {
      emoji: "🔴",
      title: "High lifestyle stress detected",
      message:
        "Your body may be under significant physiological strain. Poor sleep, low activity, or high stress levels are dragging your score down. Prioritize rest and gentle movement today.",
      color: "text-destructive",
      bg: "bg-destructive/10 border-destructive/30",
    };
  };

  const insight = getInsight(score);

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="sticky top-0 z-10 border-b border-border bg-background">
        <div className="flex items-center justify-between p-3">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/home")}
              className="h-8 w-8"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-base font-semibold text-foreground">
              Lifestyle Synergy Score
            </h1>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Today's Stats */}
        <Card className="from-chart-2/10 to-chart-2/5 rounded-xl border border-border/50 bg-gradient-to-br p-6 shadow-sm">
          <div className="text-center">
            <p className="mb-2 text-sm text-muted-foreground">
              Today's Lifestyle Synergy Score
            </p>
            <div className="text-chart-2 mb-2 text-5xl font-bold">68</div>
            <p className="text-sm text-muted-foreground">of 100 maximum</p>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-muted">
              <div
                className="bg-chart-2 h-full rounded-full"
                style={{ width: "68%" }}
              />
            </div>
          </div>
        </Card>

        {/* Insight Message */}
        <div
          className={`flex items-start gap-3 rounded-xl border p-4 ${insight.bg}`}
        >
          <span className="mt-0.5 text-2xl leading-none">{insight.emoji}</span>
          <div>
            <div className={`mb-1 text-sm font-semibold ${insight.color}`}>
              {insight.title}
            </div>
            <p className="text-xs leading-relaxed text-muted-foreground">
              {insight.message}
            </p>
          </div>
        </div>

        {/* What is Lifestyle Synergy Score */}
        <Card className="from-chart-2/5 to-chart-2/10 rounded-xl border border-border/50 bg-gradient-to-br p-5 shadow-sm">
          <h3 className="mb-3 flex items-center gap-2 font-bold text-foreground">
            <Activity className="text-chart-2 h-5 w-5" />
            What is Lifestyle Synergy Score?
          </h3>
          <p className="mb-4 text-sm leading-relaxed text-muted-foreground">
            Lifestyle Synergy Score measures how well your daily habits and
            fitness activity support overall health, on a scale of 0-100. It's
            calculated entirely from your Fitbit data, reflecting your physical
            activity, sleep quality, and recovery patterns.
          </p>
          <div className="space-y-2 rounded-lg bg-background/60 p-4">
            <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-foreground">
              Calculated From
            </h4>
            <ul className="space-y-2 text-xs text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-chart-2 mt-0.5">•</span>
                <span>
                  <span className="font-medium text-foreground">
                    Activity Score:
                  </span>{" "}
                  Steps, active minutes, calories burned
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-chart-2 mt-0.5">•</span>
                <span>
                  <span className="font-medium text-foreground">
                    Sleep & Recovery:
                  </span>{" "}
                  Sleep duration, HRV, resting HR
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-chart-2 mt-0.5">•</span>
                <span>
                  <span className="font-medium text-foreground">
                    Stress & Readiness:
                  </span>{" "}
                  Stress score, breathing rate, SpO2
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-chart-2 mt-0.5">•</span>
                <span>
                  <span className="font-medium text-foreground">
                    Metabolic Stability:
                  </span>{" "}
                  Heart rate patterns, temperature variation
                </span>
              </li>
            </ul>
          </div>
        </Card>

        {/* Score Ranges */}
        <Card className="rounded-xl border border-border/50 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">Score Ranges</h3>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Excellent</span>
              <span className="text-chart-2 font-semibold">80-100</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Good</span>
              <span className="font-semibold">60-79</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Fair</span>
              <span className="font-semibold">40-59</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Needs Improvement</span>
              <span className="font-semibold text-destructive">0-39</span>
            </div>
          </div>
        </Card>

        {/* Improvement Tips */}
        <Card className="rounded-xl border border-border/50 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">Ways to Improve</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-chart-2 mt-0.5">•</span>
              <span>Aim for 7-9 hours of quality sleep each night</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-2 mt-0.5">•</span>
              <span>
                Include at least 30 minutes of moderate exercise daily
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-2 mt-0.5">•</span>
              <span>
                Practice stress management through meditation or breathing
                exercises
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-2 mt-0.5">•</span>
              <span>Maintain consistent meal and sleep schedules</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default LifestyleSynergyDetail;
